/**
 * Asset Column Configuration
 *
 * Provides column definitions and configuration for the asset table.
 * Follows the pattern established in customerManagement/components/getColumns.tsx
 *
 * Column widths migrated to central registry (Day 3 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Rollback instructions (if needed):
 * 1. Revert this file to previous commit
 * 2. Central registry remains (no deletion needed)
 */

import React from 'react';

import { getColumnWidth } from '@/components/table/columns';
import { ColumnPriority, Columns } from '@/components/table/types';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import {
  ApplicationCell,
  AssetNameCell,
  AssetTypeCell,
  AttackSurfaceCell,
  OriginCell,
  TagsCell,
} from '@/sections/asset/components/cells';
// PortCell and ProtocolCell imports removed - columns are commented out
import {
  ASSET_DEFAULT_COLUMN_ORDER,
  isColumnSortable,
  REQUIRED_COLUMN_ID,
} from '@/sections/asset/config/columns';
import type { Asset } from '@/types';
import { normalizeAsnameValue } from '@/utils/statisticValidation.util';

/**
 * Returns the column definitions for the asset table
 *
 * @returns Array of column configurations for Table component
 */
export const getColumns = () => [
  {
    label: 'Status',
    id: 'status',
    cell: (asset: Asset) => <AssetStatusComponent asset={asset} />,
    ...getColumnWidth('asset', 'status'),
    sorting: isColumnSortable('status'),
    responsive: {
      priority: ColumnPriority.CRITICAL, // Critical indicator - always visible
      alwaysVisible: true,
    },
  },
  {
    label: 'Asset',
    id: 'identifier',
    ...getColumnWidth('asset', 'identifier'),
    cell: (asset: Asset) => <AssetNameCell asset={asset} />,
    sorting: isColumnSortable('identifier'),
    responsive: {
      priority: ColumnPriority.CRITICAL, // Never hide - primary identifier
      alwaysVisible: true,
    },
  },
  {
    label: 'Type',
    id: 'type',
    ...getColumnWidth('asset', 'type'),
    cell: (asset: Asset) => <AssetTypeCell asset={asset} />,
    sorting: isColumnSortable('type'),
    responsive: {
      priority: ColumnPriority.HIGH, // Disambiguates the heterogeneous /my bucket
    },
  },
  {
    label: 'Application',
    id: 'application',
    ...getColumnWidth('asset', 'application'),
    cell: (asset: Asset) => <ApplicationCell asset={asset} />,
    sorting: isColumnSortable('application'),
    responsive: {
      priority: ColumnPriority.HIGH, // Important rollup-root annotation
    },
  },
  {
    id: 'asname',
    label: 'AS Name',
    copy: true,
    ...getColumnWidth('asset', 'asname'),
    cell: (asset: Asset) =>
      asset.asname ? normalizeAsnameValue(asset.asname) : '-',
    sorting: isColumnSortable('asname'),
    responsive: {
      priority: ColumnPriority.LOW, // Hide early
      minViewportWidth: 1024, // Hide on tablet and below
    },
  },
  {
    id: 'asnumber',
    label: 'AS Number',
    copy: true,
    ...getColumnWidth('asset', 'asnumber'),
    cell: (asset: Asset) => asset.asnumber || '-',
    sorting: isColumnSortable('asnumber'),
    responsive: {
      priority: ColumnPriority.LOW, // Hide early
      minViewportWidth: 1024, // Hide on tablet and below
    },
  },
  {
    id: 'country',
    label: 'Country',
    copy: true,
    ...getColumnWidth('asset', 'country'),
    cell: (asset: Asset) => asset.country || '-',
    sorting: isColumnSortable('country'),
    responsive: {
      priority: ColumnPriority.LOW, // Hide early
      minViewportWidth: 900, // Hide on smaller screens
    },
  },
  {
    id: 'ipVersion',
    label: 'IP Version',
    copy: true,
    ...getColumnWidth('asset', 'ipVersion'),
    cell: (asset: Asset) => {
      // Determine IP version from class field
      if (asset.class) {
        const lowerClass = asset.class.toLowerCase();
        if (lowerClass === 'ipv4') return 'IPv4';
        if (lowerClass === 'ipv6') return 'IPv6';
      }
      return '-';
    },
    sorting: isColumnSortable('ipVersion'),
    responsive: {
      priority: ColumnPriority.LOW, // Hide early
      minViewportWidth: 900, // Hide on smaller screens
    },
  },
  {
    id: 'attackSurface',
    label: 'Attack Surface',
    copy: false,
    ...getColumnWidth('asset', 'attackSurface'),
    cell: (asset: Asset) => <AttackSurfaceCell asset={asset} />,
    sorting: isColumnSortable('attackSurface'),
    responsive: {
      priority: ColumnPriority.HIGH, // Important security indicator - hide others first
    },
  },
  {
    id: 'origins',
    label: 'Origin',
    copy: false,
    ...getColumnWidth('asset', 'origins'),
    cell: (asset: Asset) => <OriginCell asset={asset} />,
    sorting: isColumnSortable('origins'),
    className: 'overflow-visible', // Allow tooltips to display properly
    responsive: {
      priority: ColumnPriority.MEDIUM, // Hide in middle
    },
  },
  // NOTE: Port and Protocol columns are commented out per user request
  // These columns are rarely used and add clutter to the column selector
  // Cell components (PortCell, ProtocolCell) remain available if needed in future
  // Uncommenting these will make them available in the "Available Columns" list
  // {
  //   id: 'port',
  //   label: 'Port',
  //   copy: true,
  //   ...getColumnWidth('asset', 'port'),
  //   cell: (asset: Asset) => <PortCell asset={asset} />,
  //   sorting: isColumnSortable('port'),
  // },
  // {
  //   id: 'protocol',
  //   label: 'Protocol',
  //   copy: true,
  //   ...getColumnWidth('asset', 'protocol'),
  //   cell: (asset: Asset) => <ProtocolCell asset={asset} />,
  //   sorting: isColumnSortable('protocol'),
  // },
  {
    id: 'profile',
    label: 'Scan Profile',
    copy: true,
    ...getColumnWidth('asset', 'profile'),
    cell: (asset: Asset) => asset.profile || 'Default',
    sorting: isColumnSortable('profile'),
    responsive: {
      priority: ColumnPriority.LOW,
      minViewportWidth: 1200,
    },
  },
  {
    id: 'tags',
    label: 'Tags',
    copy: false,
    ...getColumnWidth('asset', 'tags'),
    cell: (asset: Asset) => <TagsCell asset={asset} />,
    sorting: isColumnSortable('tags'),
    responsive: {
      priority: ColumnPriority.LOW, // Hide first
      minViewportWidth: 1200, // Hide on anything below wide desktop
    },
  },
  {
    id: 'created',
    label: 'First Seen',
    copy: true,
    ...getColumnWidth('asset', 'created'),
    cell: 'date',
    sorting: isColumnSortable('created'),
    responsive: {
      priority: ColumnPriority.MEDIUM, // Hide in middle
      minViewportWidth: 1024, // Hide on tablet and below
    },
  },
  {
    label: 'Last Seen',
    id: 'visited',
    copy: true,
    ...getColumnWidth('asset', 'visited'),
    cell: 'date',
    sorting: isColumnSortable('visited'),
    responsive: {
      priority: ColumnPriority.HIGH, // Hide after CRITICAL columns
      minViewportWidth: 900, // Hide on smaller screens
    },
  },
];

/**
 * Generates sortable columns configuration for the asset table
 *
 * Creates active and available column lists with proper defaults.
 * - The identifier column is always required and cannot be removed (disabled: true)
 * - Default order: Status first, then core columns, then date columns last
 * - Users can reorder, remove (except identifier), and add any columns
 *
 * @param defaultColumns - The column definitions from getColumns()
 * @returns Configuration object for useSortableColumn hook
 */
export const getSortableColumnsConfig = ({
  defaultColumns,
}: {
  defaultColumns: Columns<Asset>;
}) => {
  // Create active columns from default order
  // All columns are removable and reorderable except 'identifier'
  const activeColumns = ASSET_DEFAULT_COLUMN_ORDER.map(columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (!column) return null;

    return {
      label: column.label,
      id: column.id,
      disabled: columnId === REQUIRED_COLUMN_ID, // Only identifier is required
      isDefault: columnId === REQUIRED_COLUMN_ID,
    };
  }).filter((item): item is NonNullable<typeof item> => item !== null);

  // Create available columns (not in default order)
  const defaultOrderSet = new Set<string>(
    ASSET_DEFAULT_COLUMN_ORDER as readonly string[]
  );
  const availableColumnIds = defaultColumns
    .map(col => col.id as string)
    .filter(id => !defaultOrderSet.has(id));

  const availableColumns = availableColumnIds
    .map(columnId => {
      const column = defaultColumns.find(col => col.id === columnId);
      if (!column) return null;

      return {
        label: column.label,
        id: column.id,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    fixed: [],
    active: activeColumns,
    available: availableColumns,
  };
};
